import { z } from 'zod';
import { isLastCommaFirst } from '../name.js';
import { cleanString } from './clean.js';
import { CompanyType, DocumentKind, OnboardingState } from './onboarding-enums.js';

// Every free-text name/address field below is wrapped in cleanString() (T-200):
// no-break spaces, zero-width chars, U+FFFD and control chars are normalized
// away BEFORE the length/format rules run, so a pasted value is stored the way
// it was meant to be typed.

// Resource names must be entered "Last, First" (matches the billing Worker
// column); naturalName() flips them to "First Last" for prose downstream.
const ResourceName = cleanString(
  z.string().min(1).max(200).refine(isLastCommaFirst, 'Enter as Last, First (include a comma)'),
);

// ISO date string (YYYY-MM-DD) — what HTML <input type="date"> emits and
// what Postgres' date type round-trips cleanly.
const DateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');

export const OnboardingCreateInput = z.object({
  admin_resource_name: ResourceName,
  admin_business_email: z.string().email(),
  admin_role: cleanString(z.string().max(200)).nullable().optional(),
  client_id: z.string().uuid(),
  // Set to add the resource to an EXISTING vendor (resource-only onboarding);
  // null/omitted = the normal new-vendor path (vendor created at submit by EIN).
  vendor_id: z.string().uuid().nullable().optional(),
  rate_to_contractor: z.number().positive(),
  rate_to_client: z.number().positive().nullable().optional(),
  // The vendor who referred this engagement (T-109) — an existing vendor,
  // distinct from vendor_id (the business). Engagement-grain: set per
  // onboarding, copied to the engagement at completion. null/omitted = not a
  // referral. The owner-only referral_fee_per_hour is deferred to Wave 3.
  referred_by_vendor_id: z.string().uuid().nullable().optional(),
  start_date: DateString,
  end_date: DateString,
  product_service: cleanString(z.string().min(1).max(400)),
  // Resource personal details captured UP FRONT at acceptance (T-112) — the
  // resource's own email + shipping address, needed early for the RCF/laptop
  // order (T-115) and Checkr (T-116) before the vendor fills the magic-link
  // form. Optional: blank falls through to the vendor step as today. Seeded
  // into draft_data (reusing resource_email / resource_mailing_address_*, no
  // new columns) so they pre-fill the contractor form, editable there.
  admin_resource_email: z.string().email().optional(),
  admin_resource_mailing_address_street: cleanString(z.string().max(200)).optional(),
  admin_resource_mailing_address_city: cleanString(z.string().max(120)).optional(),
  admin_resource_mailing_address_state: cleanString(z.string().max(60)).optional(),
  admin_resource_mailing_address_zip: cleanString(z.string().max(20)).optional(),
  // Resource mobile phone captured up front (T-115) for the RCF email body;
  // seeds resource_mobile_phone in the draft (reuses resources.mobile_phone).
  admin_resource_mobile_phone: z
    .string()
    .regex(/^\d{3}-\d{3}-\d{4}$/, 'Format: xxx-xxx-xxxx')
    .optional(),
  // Mint the resource's uIT O365 mailbox at create time (T-140), in parallel
  // with onboarding and before signing. Default on; off skips provisioning
  // (e.g. resource-only or placeholder onboardings).
  create_uit_mailbox: z.boolean().default(true),
});
export type OnboardingCreateInput = z.infer<typeof OnboardingCreateInput>;

// Client summary embedded in onboarding responses — saves the FE a separate
// fetch for the dropdown label.
const ClientSummary = z.object({
  id: z.string().uuid(),
  name: z.string(),
  project_or_product_name: z.string(),
});

const OnboardingBase = z.object({
  id: z.string().uuid(),
  state: OnboardingState,
  admin_resource_name: z.string(),
  admin_business_email: z.string().email(),
  admin_role: z.string().nullable(),
  client: ClientSummary,
  // Set when this is a resource-only onboarding under an existing vendor; the
  // queue surfaces vendor_name (null in the new-vendor path until submit).
  vendor_id: z.string().uuid().nullable(),
  vendor_name: z.string().nullable(),
  rate_to_contractor: z.number(),
  start_date: DateString,
  end_date: DateString,
  product_service: z.string(),
  esign_envelope_id: z.string().nullable(),
  // uIT O365 mailbox minted at create (T-140); null until provisioned (toggle
  // off / Graph unconfigured / failed). Copied onto resources.uit_email at submit.
  uit_email: z.string().nullable(),
  create_uit_mailbox: z.boolean(),
  // Cigna manager for the pre-submit RCF (T-115); set on the review-and-send
  // screen, copied to the engagement at submit. Null until selected.
  cigna_manager_id: z.string().uuid().nullable(),
  magic_link_expires_at: z.string(),
  // Populated ONLY on create + resend (the plaintext token is unavailable
  // afterward). No-email-mode affordance so the admin can copy the link.
  magic_link_url: z.string().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

// Full response — visible to an OWNER only (T-186). Beside rate_to_contractor,
// rate_to_client is the margin, so it rides the owner capability rather than the
// admin role.
export const OnboardingResponse = OnboardingBase.extend({
  rate_to_client: z.number().nullable(),
});
export type OnboardingResponse = z.infer<typeof OnboardingResponse>;

// Masked response — every non-owner, including a plain admin. The field is
// OMITTED, not nulled, so the wire shape genuinely differs.
export const OnboardingResponseMasked = OnboardingBase;
export type OnboardingResponseMasked = z.infer<typeof OnboardingResponseMasked>;

export const OnboardingCreatedResponse = z.union([OnboardingResponse, OnboardingResponseMasked]);
export type OnboardingCreatedResponse = z.infer<typeof OnboardingCreatedResponse>;

// ── Contractor-facing onboarding ─────────────────────────────────────
// Subset of contractor-side fields, validated loosely for autosave and
// strictly for submit. See REQUIREMENTS.md "Contractor-entered fields".

const Phone = z.string().regex(/^\d{3}-\d{3}-\d{4}$/, 'Format: xxx-xxx-xxxx');
const AchRouting = z.string().regex(/^\d{9}$/, 'Must be 9 digits');
const AchAccount = z.string().regex(/^\d{5,17}$/, 'Must be 5–17 digits');
const Ein = z.string().regex(/^\d{2}-\d{7}$/, 'Format: xx-xxxxxxx');

// The resource (person) section — the ONLY fields collected when adding a
// resource to an existing vendor (resource-only onboarding, T-062).
const ResourceContactFields = z.object({
  // The individual resource's email, contractor-entered → resources.resource_email.
  resource_email: z.string().email(),
  resource_mailing_address_street: cleanString(z.string().min(1).max(200)),
  resource_mailing_address_city: cleanString(z.string().min(1).max(120)),
  resource_mailing_address_state: cleanString(z.string().min(1).max(60)),
  resource_mailing_address_zip: cleanString(z.string().min(1).max(20)),
  // The resource's own mobile (NEW, T-062) → resources.mobile_phone. Optional:
  // the contract doesn't require it, but the Resource section now collects it.
  resource_mobile_phone: Phone.optional(),
  // Contract signer override (T-130): the magic-link recipient can redirect the
  // subcontractor agreement to a different signer (e.g. the vendor owner). The
  // form pre-fills these (resource natural name / business email) and they are
  // editable; omitted/blank falls back to those defaults at submit. In BOTH
  // flows (new-vendor and resource-only).
  contract_signer_name: cleanString(z.string().min(1).max(200)).optional(),
  contract_signer_email: z.string().email().optional(),
});

// The business (vendor) section — collected only in the full new-vendor flow.
const BusinessFields = z.object({
  business_name: cleanString(z.string().min(1).max(200)),
  business_address_street: cleanString(z.string().min(1).max(200)),
  business_address_city: cleanString(z.string().min(1).max(120)),
  business_address_state: cleanString(z.string().min(1).max(60)),
  business_address_zip: cleanString(z.string().min(1).max(20)),
  // The contract has both; only mobile is mandatory (same value is fine).
  mobile_phone: Phone,
  office_phone: Phone.optional(),
  // The BUSINESS email: the contract destination. Admin-entered at onboarding
  // (onboardings.admin_business_email) and pre-filled into this field, but the
  // contractor can OVERRIDE it here so the contract goes to a different inbox
  // (T-055). Persisted to vendors.business_email; drives the esign signer.
  business_email: z.string().email(),
  // Required at submit; the UI defaults it to the business-address state.
  state_of_incorporation: cleanString(z.string().min(1).max(60)),
  ach_routing: AchRouting,
  ach_account: AchAccount,
  ein: Ein,
  company_type: CompanyType,
});

const ContractorFields = BusinessFields.merge(ResourceContactFields);

// Autosave: every field optional; the contractor saves as they type.
export const OnboardingContractorDraftPatch = ContractorFields.partial();
export type OnboardingContractorDraftPatch = z.infer<typeof OnboardingContractorDraftPatch>;

// Submit (full new-vendor flow): everything required except the optionals.
export const OnboardingContractorSubmit = ContractorFields;
export type OnboardingContractorSubmit = z.infer<typeof OnboardingContractorSubmit>;

// Submit (resource-only flow): just the resource section — the vendor already
// exists, so business/banking/EIN are not collected.
export const OnboardingResourceOnlySubmit = ResourceContactFields;
export type OnboardingResourceOnlySubmit = z.infer<typeof OnboardingResourceOnlySubmit>;

const DocumentSummary = z.object({
  id: z.string().uuid(),
  kind: DocumentKind,
  original_filename: z.string(),
  size_bytes: z.number().int().nonnegative(),
  content_type: z.string(),
  created_at: z.string(),
});
export type DocumentSummary = z.infer<typeof DocumentSummary>;

// Public response for the contractor's view of the onboarding (token-scoped).
// Admin's uit_contact_email, product_service, rate_to_client, and admin_role
// are intentionally NOT exposed here per REQUIREMENTS.md. business_email is the
// admin-entered contact/business email (the magic-link + contract destination).
export const OnboardingPublicResponse = z.object({
  state: OnboardingState,
  editable: z.boolean(),
  // Resource-only mode (adding a resource to an existing vendor): the form hides
  // the Business / Banking sections and collects only the resource's fields.
  resource_only: z.boolean(),
  // The existing vendor's name when resource_only — shown as context on the form.
  vendor_business_name: z.string().nullable(),
  contractor_resource_name: z.string(),
  business_email: z.string().email(),
  rate_to_contractor: z.number(),
  start_date: DateString,
  end_date: DateString,
  client: z.object({
    name: z.string(),
    project_or_product_name: z.string(),
  }),
  magic_link_expires_at: z.string(),
  draft: OnboardingContractorDraftPatch,
  documents: z.array(DocumentSummary),
});
export type OnboardingPublicResponse = z.infer<typeof OnboardingPublicResponse>;

// Document kinds the contractor is permitted to upload. (resume = admin;
// signed_contract = e-sign webhook; others = misc/legacy and not contractor-driven.)
export const ContractorUploadKind = z.enum([
  'w9',
  'voided_check',
  'coi',
  'visa',
  'cert_of_incorporation',
  'driver_license',
]);
export type ContractorUploadKind = z.infer<typeof ContractorUploadKind>;
